import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Box, Chip, InputBase, Typography } from '@mui/material';
import { ACCENT, ACCENT_TINT } from '../_shared';
import type { WizardData } from './wizardTypes';

interface Props {
    data: WizardData;
    onChange: (patch: Partial<WizardData>) => void;
    compact?: boolean;
}

const Step3Keywords = ({ data, onChange, compact }: Props) => {
    const [draft, setDraft] = useState('');

    const appendKeywords = (rawValues: string[]) => {
        const next = [...data.keywords];
        let changed = false;
        for (const raw of rawValues) {
            const value = raw.trim();
            if (!value) continue;
            if (next.includes(value)) continue;
            next.push(value);
            changed = true;
        }
        if (changed) onChange({ keywords: next });
    };

    const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
        const value = ev.target.value;
        if (!value.includes(',')) {
            setDraft(value);
            return;
        }
        // 한글 IME 확정 직후 ',' 가 입력값에 섞여 들어오는 경우 — 분리해서 등록
        const parts = value.split(',');
        const completed = parts.slice(0, -1);
        const remaining = parts[parts.length - 1] ?? '';
        appendKeywords(completed);
        setDraft(remaining);
    };

    const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
        // 한글 IME 조합 중인 키 이벤트는 무시 — Enter / ',' 가 IME 확정용으로 한 번 더 dispatch 되는 중복 처리 방지
        if (ev.nativeEvent.isComposing || ev.keyCode === 229) return;

        if (ev.key === 'Enter' || ev.key === ',') {
            ev.preventDefault();
            appendKeywords([draft]);
            setDraft('');
            return;
        }
        if (ev.key === 'Backspace' && draft === '' && data.keywords.length > 0) {
            onChange({ keywords: data.keywords.slice(0, -1) });
        }
    };

    const removeKeyword = (index: number) => {
        onChange({ keywords: data.keywords.filter((_, i) => i !== index) });
    };

    const triggerLabel =
        data.triggerType === 'COMMENT_KEYWORD'
            ? '댓글'
            : data.triggerType === 'DM_KEYWORD'
              ? 'DM'
              : data.triggerType === 'STORY_REPLY'
                ? '스토리 답장'
                : '메시지';
    const isOptional = data.triggerType === 'STORY_REPLY';

    return (
        <Box>
            {!compact && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {isOptional ? '어떤 단어가 포함된 답장만 받을까요? (선택)' : '어떤 약속 단어를 감지할까요?'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                        {isOptional ? (
                            <>
                                비워두면 모든 스토리 답장에 응답해요.
                                <br />
                                특정 단어가 포함된 답장만 응답하려면 입력하세요.
                            </>
                        ) : (
                            <>
                                {triggerLabel}에 이 단어가 포함되면 자동화가 실행돼요.
                                <br />
                                Enter 또는 콤마(,)로 여러 개 등록할 수 있어요. 대소문자는 구분하지 않습니다.
                            </>
                        )}
                    </Typography>
                </>
            )}

            <Box
                onClick={(ev) => (ev.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    cursor: 'text',
                    transition: 'border-color 120ms ease',
                    '&:focus-within': { borderColor: ACCENT },
                }}
            >
                {data.keywords.map((kw, i) => (
                    <Chip
                        key={`${kw}-${i}`}
                        label={kw}
                        size="small"
                        onDelete={() => removeKeyword(i)}
                        sx={{
                            bgcolor: ACCENT_TINT,
                            color: ACCENT,
                            fontWeight: 600,
                            '& .MuiChip-deleteIcon': { color: ACCENT, '&:hover': { color: ACCENT } },
                        }}
                    />
                ))}
                <InputBase
                    autoFocus={!compact}
                    value={draft}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={() => {
                        if (draft.trim()) {
                            appendKeywords([draft]);
                            setDraft('');
                        }
                    }}
                    placeholder={data.keywords.length === 0 ? '예) 가이드, 비타민, 쿠폰' : '계속 추가…'}
                    sx={{ flex: 1, minWidth: 120, px: 1, fontSize: 14 }}
                />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {isOptional
                    ? '비워두면 모든 스토리 답장에 응답합니다. 입력 시 부분 포함으로 매칭돼요.'
                    : '부분 포함으로 매칭됩니다. 예: "비타민" 등록 시 "비타민제" 댓글도 발동돼요.'}
            </Typography>
        </Box>
    );
};

export default Step3Keywords;
